import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const useOpportunityData = (opportunityId, activeTab) => {
    const { currentUser } = useAuth();
    
    // 基本データ
    const [opportunity, setOpportunity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 活動一覧State
    const [meetings, setMeetings] = useState([]);
    const [meetingsPage, setMeetingsPage] = useState(1);
    const [meetingsTotal, setMeetingsTotal] = useState(0);
    const [meetingsTotalPages, setMeetingsTotalPages] = useState(0);
    const meetingsPerPage = 20;

    // ステージマスタState
    const [opportunityStages, setOpportunityStages] = useState([]);

    // 提案求人一覧State
    const [proposedJobs, setProposedJobs] = useState([]);
    const [jobsPage, setJobsPage] = useState(1);
    const [jobsTotal, setJobsTotal] = useState(0);
    const [jobsTotalPages, setJobsTotalPages] = useState(0);
    const jobsPerPage = 20;

    // モーダル用: 全提案求人
    const [allProposedJobs, setAllProposedJobs] = useState([]);

    const fetchOpportunityDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.fetch(`/opportunities/${opportunityId}`);
            if (!response) return; // Redirected
            if (!response.ok) throw new Error('案件詳細の取得に失敗しました。');
            const data = await response.json();
            setOpportunity(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [opportunityId]);

    const fetchMeetings = useCallback(async () => {
        try {
            const response = await api.fetch(
                `/opportunities/${opportunityId}/meetings?page=${meetingsPage}&per_page=${meetingsPerPage}`
            );
            if (response && response.ok) {
                const data = await response.json();
                setMeetings(data.items);
                setMeetingsTotal(data.total);
                setMeetingsTotalPages(data.total_pages);
            }
        } catch (err) {
            console.error('Failed to fetch meetings:', err);
        }
    }, [opportunityId, meetingsPage, meetingsPerPage]);

    const fetchProposedJobs = useCallback(async () => {
        try {
            const response = await api.fetch(
                `/opportunities/${opportunityId}/proposed_jobs?page=${jobsPage}&per_page=${jobsPerPage}`
            );
            if (response && response.ok) {
                const data = await response.json();
                setProposedJobs(data.items);
                setJobsTotal(data.total);
                setJobsTotalPages(data.total_pages);
            }
        } catch (err) {
            console.error('Failed to fetch proposed jobs:', err);
        }
    }, [opportunityId, jobsPage, jobsPerPage]);

    // モーダル用: 全提案求人を一括取得
    const fetchAllProposedJobs = useCallback(async () => {
        try {
            const response = await api.fetch(
                `/opportunities/${opportunityId}/proposed_jobs?page=1&per_page=300`
            );
            if (response && response.ok) {
                const data = await response.json();
                setAllProposedJobs(data.items);
            }
        } catch (err) {
            console.error('Failed to fetch all proposed jobs:', err);
        }
    }, [opportunityId]);

    // ステージマスタを一括取得
    const fetchOpportunityStages = useCallback(async () => {
        try {
            const response = await api.fetch('/opportunities/stages');
            if (response && response.ok) {
                const data = await response.json();
                setOpportunityStages(data);
            }
        } catch (err) {
            console.error('Failed to fetch opportunity stages:', err);
        }
    }, []);

    // 初回マウント/ID変更時に基本データをフェッチ（ページネーション非依存）
    useEffect(() => {
        fetchOpportunityDetail();
        fetchAllProposedJobs();
        fetchOpportunityStages();
    }, [fetchOpportunityDetail, fetchAllProposedJobs, fetchOpportunityStages]);

    // 活動一覧: ページ変更 or タブがアクティブになった時にフェッチ
    useEffect(() => {
        fetchMeetings();
    }, [fetchMeetings]);

    // 提案求人一覧: ページ変更 or タブがアクティブになった時にフェッチ
    useEffect(() => {
        fetchProposedJobs();
    }, [fetchProposedJobs]);

    return {
        opportunity,
        setOpportunity,
        loading,
        setLoading,
        error,
        currentUser,
        // 活動一覧関連
        meetings,
        meetingsPage,
        setMeetingsPage,
        meetingsTotal,
        meetingsTotalPages,
        meetingsPerPage,
        fetchMeetings,
        // 提案求人関連
        proposedJobs,
        jobsPage,
        setJobsPage,
        jobsTotal,
        jobsTotalPages,
        jobsPerPage,
        fetchProposedJobs,
        // モーダル用
        allProposedJobs,
        fetchAllProposedJobs,
        // ステージマスタ
        opportunityStages
    };
};

export default useOpportunityData;
